type MessageHandler = (data: any) => void;

class WebSocketClient {
  private ws: WebSocket | null = null;
  private handlers = new Map<string, Set<MessageHandler>>();
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectDelay = 1000;
  private maxReconnectDelay = 30000;
  private pendingMessages: string[] = [];
  private _isConnected = false;
  private _currentRole: string | null = null;
  private _currentUserId: string | null = null;

  get isConnected() { return this._isConnected; }

  connect(role: "customer" | "admin", userId: string) {
    // If already connected with SAME role, skip
    if (this.ws?.readyState === WebSocket.OPEN && this._currentRole === role && this._currentUserId === userId) {
      return;
    }

    // If connected with DIFFERENT role, close and reconnect
    if (this.ws && this._currentRole !== role) {
      this.ws.onclose = null; // prevent reconnect loop
      this.ws.close();
      this.ws = null;
      this._isConnected = false;
    }

    if (this.ws?.readyState === WebSocket.OPEN) return;

    this._currentRole = role;
    this._currentUserId = userId;

    const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    this.ws = new WebSocket(`${protocol}//${window.location.host}/ws`);

    this.ws.onopen = () => {
      this._isConnected = true;
      this.reconnectDelay = 1000;
      this.send({ type: "auth", role, userId });
      for (const msg of this.pendingMessages) {
        this.ws!.send(msg);
      }
      this.pendingMessages = [];
      this.emit("connected", {});
    };

    this.ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        this.emit(data.type, data);
      } catch { /* ignore malformed */ }
    };

    this.ws.onclose = () => {
      this._isConnected = false;
      this.emit("disconnected", {});
      this.scheduleReconnect(role, userId);
    };

    this.ws.onerror = () => {
      this.ws?.close();
    };
  }

  disconnect() {
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    if (this.ws) {
      this.ws.onclose = null;
      this.ws.close();
    }
    this.ws = null;
    this._isConnected = false;
    this._currentRole = null;
    this._currentUserId = null;
  }

  send(data: object) {
    const msg = JSON.stringify(data);
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(msg);
    } else {
      this.pendingMessages.push(msg);
    }
  }

  on(type: string, handler: MessageHandler) {
    if (!this.handlers.has(type)) this.handlers.set(type, new Set());
    this.handlers.get(type)!.add(handler);
    return () => { this.handlers.get(type)?.delete(handler); };
  }

  private emit(type: string, data: any) {
    this.handlers.get(type)?.forEach((h) => h(data));
    this.handlers.get("*")?.forEach((h) => h(data));
  }

  private scheduleReconnect(role: "customer" | "admin", userId: string) {
    this.reconnectTimer = setTimeout(() => {
      this.reconnectDelay = Math.min(this.reconnectDelay * 2, this.maxReconnectDelay);
      this.connect(role, userId);
    }, this.reconnectDelay);
  }
}

export const wsClient = new WebSocketClient();
